import { createBaseApi } from '../baseApi';
import { API_ENDPOINTS } from '../../../utils/config';

/**
 * RTK Query API for the SUSU Closet try-on earnings feature.
 * Handles fetching the user's earnings summary and paginated history
 * from sharing closet items that other users try on.
 * 
 * Updated to support real money earnings (USD) alongside credits.
 */
const earningsApi = createBaseApi('earningsApi');

// Enhanced API with earnings endpoints
export const enhancedEarningsApi = earningsApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Get the current user's earnings summary.
     * Returns both credit-based and real money earnings data.
     */
    getEarningsSummary: builder.query({
      query: () => ({
        url: API_ENDPOINTS.EARNINGS.SUMMARY,
        method: 'GET'
      }),
      providesTags: ['Earnings'],
      transformResponse: (response) => ({
        // Money is the unit creators are paid in; the credit fields below are
        // legacy and only populated on rows written before payouts were cash.
        availableUsd: Number(response.availableUsd || 0),
        paidOutUsd: Number(response.paidOutUsd || 0),
        lifetimeUsd: Number(response.lifetimeUsd || 0),
        payoutPerTryOnUsd: Number(response.payoutPerTryOnUsd || 0),
        totalEarned: response.totalEarned || 0,
        lifetimeEarned: response.lifetimeEarned || 0,
        pendingBalance: response.pendingBalance || 0,
        lifetimePayout: response.lifetimePayout || 0,
        totalItemsShared: response.totalItemsShared || 0,
        totalTryOnsReceived: response.totalTryOnsReceived || 0,
        recentEarnings: response.recentEarnings || []
      })
    }),

    /**
     * Balance owed plus the reasons a payout can't run yet.
     */
    getPayoutStatus: builder.query({
      query: () => ({ url: API_ENDPOINTS.EARNINGS.PAYOUT_STATUS, method: 'GET' }),
      providesTags: ['Earnings']
    }),

    /**
     * Opt in or out of monthly automatic payouts.
     */
    setAutoPayout: builder.mutation({
      query: (enabled) => ({
        url: API_ENDPOINTS.EARNINGS.AUTO_PAYOUT,
        method: 'PUT',
        body: { enabled }
      }),
      // The endpoint returns the recomputed eligibility, so write it straight
      // into the cache. Relying on tag invalidation alone left the switch
      // rendering its old position after a successful save.
      async onQueryStarted(_enabled, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            enhancedEarningsApi.util.updateQueryData('getPayoutStatus', undefined, () => data)
          );
        } catch {
          /* the mutation's own error handling surfaces this */
        }
      }
    }),

    /**
     * Create the creator's Stripe Connect account (if needed) and return the
     * hosted onboarding URL to send them to.
     */
    startPayoutOnboarding: builder.mutation({
      queryFn: async (_arg, _api, _extra, baseQuery) => {
        const created = await baseQuery({
          url: API_ENDPOINTS.STRIPE_CONNECT.CREATE_ACCOUNT,
          method: 'POST',
          body: {}
        });
        if (created.error) return { error: created.error };

        const accountId =
          created.data?.account?.id || created.data?.accountId || created.data?.id;

        const session = await baseQuery({
          url: API_ENDPOINTS.STRIPE_CONNECT.ACCOUNT_SESSION,
          method: 'POST',
          // Onboarding is shared with SHEYOU, which is where Stripe sent people
          // back by default. Say where we came from so creators return here.
          body: {
            account: accountId,
            origin: window.location.origin,
            returnPath: '/my-closet?wallet=open'
          }
        });
        if (session.error) return { error: session.error };

        return { data: { url: session.data?.client_secret, accountId } };
      },
      invalidatesTags: ['Earnings']
    }),

    /**
     * Get the current user's paginated earnings history.
     * Each record represents a real-money earning from another user's licensed try-on.
     * @param {number} _start - Pagination offset
     * @param {number} _limit - Number of records per page
     */
    getEarningsHistory: builder.query({
      query: ({ _start = 0, _limit = 20 } = {}) => ({
        url: API_ENDPOINTS.EARNINGS.HISTORY,
        method: 'GET',
        params: { _start, _limit }
      }),
      providesTags: ['Earnings'],
      // The endpoint returns { results, pagination }. Falling through to the
      // raw response handed the component an object, and .map() on it threw —
      // which is what "View All" was hitting.
      transformResponse: (response) =>
        response?.results || response?.records || (Array.isArray(response) ? response : [])
    })
  })
});

export const {
  useGetEarningsSummaryQuery,
  useGetEarningsHistoryQuery,
  useGetPayoutStatusQuery,
  useSetAutoPayoutMutation,
  useStartPayoutOnboardingMutation
} = enhancedEarningsApi;

export default enhancedEarningsApi;
